import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { AppShell } from "@/components/AppShell";
import { StatusBadge, CompanyAvatar } from "@/components/StatusBadge";
import {
  BOLAG,
  FASER,
  formatSEK,
  totalKapital,
  fasFarg,
  rlDelta,
  type Fas,
  type Bolag,
} from "@/lib/portfolio-data";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowRight, TrendingUp, MapPin, Building2, BarChart3 } from "lucide-react";

export const Route = createFileRoute("/pipeline")({
  head: () => ({
    meta: [
      { title: "Pipeline — Inkubera" },
      { name: "description", content: "Pipelinevy: dealflow, förinkubation och inkubation." },
    ],
  }),
  component: Pipeline,
});

type Vy = "alla" | "Prospect" | "Förinkubation" | "Inkubation";

function Pipeline() {
  const [vy, setVy] = useState<Vy>("alla");

  return (
    <AppShell>
      <section className="px-8 pt-10 pb-6 border-b border-border">
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">Pipeline</div>
        <h1 className="font-display text-4xl">Resan från idé till exit</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Växla mellan översikt och fokuserade vyer för dealflow och överlämningar mellan faserna.
        </p>
      </section>

      <section className="px-8 pt-6">
        <Tabs value={vy} onValueChange={(v) => setVy(v as Vy)}>
          <TabsList className="grid grid-cols-4 w-full max-w-2xl">
            <TabsTrigger value="alla">Alla faser</TabsTrigger>
            <TabsTrigger value="Prospect">
              Prospects
              <span className="ml-1.5 text-xs opacity-60">
                {BOLAG.filter((b) => b.fas === "Prospect").length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="Förinkubation">
              Förinkubation
              <span className="ml-1.5 text-xs opacity-60">
                {BOLAG.filter((b) => b.fas === "Förinkubation").length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="Inkubation">
              Inkubation
              <span className="ml-1.5 text-xs opacity-60">
                {BOLAG.filter((b) => b.fas === "Inkubation").length}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="alla" className="mt-6">
            <AllaFaserVy />
          </TabsContent>
          <TabsContent value="Prospect" className="mt-6">
            <ProspectVy />
          </TabsContent>
          <TabsContent value="Förinkubation" className="mt-6">
            <FasFokusVy fas="Förinkubation" nästa="Inkubation" />
          </TabsContent>
          <TabsContent value="Inkubation" className="mt-6">
            <FasFokusVy fas="Inkubation" nästa="Tillväxt" />
          </TabsContent>
        </Tabs>
      </section>
    </AppShell>
  );
}

function AllaFaserVy() {
  return (
    <>
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-4 min-w-max pb-4">
          {FASER.map((f) => {
            const bolag = BOLAG.filter((b) => b.fas === f);
            return (
              <div key={f} className="w-72 shrink-0">
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className="size-2 rounded-full" style={{ background: fasFarg[f] }} />
                    <span className="font-medium">{f}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{bolag.length}</span>
                </div>
                <div
                  className="rounded-2xl p-2.5 space-y-2 min-h-32 border border-dashed border-border"
                  style={{ background: `color-mix(in oklab, ${fasFarg[f]} 5%, var(--card))` }}
                >
                  {bolag.map((b) => (
                    <BolagKortKort key={b.id} b={b} />
                  ))}
                  {bolag.length === 0 && (
                    <div className="text-xs text-muted-foreground/60 text-center py-6">Inga bolag</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-card border border-border p-6">
        <h2 className="font-display text-xl mb-4">Fas-fördelning</h2>
        <div className="flex h-3 rounded-full overflow-hidden">
          {FASER.map((f) => {
            const n = BOLAG.filter((b) => b.fas === f).length;
            const pct = (n / BOLAG.length) * 100;
            if (pct === 0) return null;
            return <div key={f} style={{ width: `${pct}%`, background: fasFarg[f] }} title={`${f}: ${n}`} />;
          })}
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {FASER.map((f) => (
            <div key={f} className="flex items-center gap-2">
              <StatusBadge fas={f} />
              <span className="text-sm text-muted-foreground">{BOLAG.filter((b) => b.fas === f).length}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function BolagKortKort({ b }: { b: Bolag }) {
  return (
    <Link
      to="/bolag/$id"
      params={{ id: b.id }}
      className="block p-3 rounded-xl bg-card border border-border hover:border-foreground/40 hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-2.5">
        <CompanyAvatar initialer={b.initialer} farg={b.logoFarg} size={32} />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate">{b.namn}</div>
          <div className="text-[11px] text-muted-foreground">{b.kommun ?? b.ort}</div>
        </div>
      </div>
      <div className="mt-2.5 flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">{b.mognad}</span>
        <span className="font-medium">{formatSEK(totalKapital(b))}</span>
      </div>
    </Link>
  );
}

// ===== Prospect-vy: dealflow med inflöde-fördelning =====
function ProspectVy() {
  const prospects = useMemo(() => BOLAG.filter((b) => b.fas === "Prospect"), []);

  const perInflode = useMemo(() => {
    const map = new Map<string, Bolag[]>();
    prospects.forEach((b) => {
      const k = b.inflöde ?? "Egen kontakt";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(b);
    });
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [prospects]);

  const perKommun = useMemo(() => {
    const map = new Map<string, number>();
    prospects.forEach((b) => {
      const k = b.kommun ?? "—";
      map.set(k, (map.get(k) ?? 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [prospects]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Nyckeltal label="Aktiva prospects" värde={prospects.length} />
        <Nyckeltal label="Unika inflödeskanaler" värde={perInflode.length} />
        <Nyckeltal label="Kommuner representerade" värde={perKommun.length} />
      </div>

      <div className="rounded-2xl bg-card border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="size-4 text-muted-foreground" />
          <h2 className="font-display text-lg">Dealflow per inflödeskanal</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Varifrån kommer prospekten innan de når Inkubera — Akademi Örebro, Almi, näringslivet eller egna kanaler.
        </p>
        <div className="space-y-5">
          {perInflode.map(([inflöde, bolag]) => (
            <div key={inflöde}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{inflöde}</Badge>
                  <span className="text-sm text-muted-foreground">{bolag.length} bolag</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {bolag.map((b) => (
                  <BolagKortKort key={b.id} b={b} />
                ))}
              </div>
            </div>
          ))}
          {perInflode.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-6">Inga prospects.</div>
          )}
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="size-4 text-muted-foreground" />
          <h2 className="font-display text-lg">Geografisk spridning</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {perKommun.map(([k, n]) => (
            <Badge key={k} variant="outline" className="text-sm">
              {k} · <span className="ml-1 font-semibold">{n}</span>
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

// ===== Förinkubation/Inkubation-vy: överlämning till nästa fas =====
function FasFokusVy({ fas, nästa }: { fas: Fas; nästa: Fas }) {
  const bolag = useMemo(() => BOLAG.filter((b) => b.fas === fas), [fas]);

  const redoForÖverlämning = useMemo(
    () =>
      bolag.filter((b) => {
        const d = rlDelta(b);
        return d !== null && d.total >= 4;
      }),
    [bolag],
  );

  const snittDelta = useMemo(() => {
    const ds = bolag.map((b) => rlDelta(b)).filter((d): d is NonNullable<ReturnType<typeof rlDelta>> => d !== null);
    if (ds.length === 0) return 0;
    return +(ds.reduce((s, d) => s + d.total, 0) / ds.length).toFixed(1);
  }, [bolag]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Nyckeltal label={`Aktiva i ${fas}`} värde={bolag.length} />
        <Nyckeltal label="Snittlig RL-rörelse" värde={`+${snittDelta}`} />
        <Nyckeltal
          label={`Kandidater för ${nästa}`}
          värde={redoForÖverlämning.length}
          ton="positive"
        />
      </div>

      <div className="rounded-2xl bg-card border border-border p-6">
        <div className="flex items-center gap-2 mb-1">
          <ArrowRight className="size-4 text-muted-foreground" />
          <h2 className="font-display text-lg">Överlämning till {nästa}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Bolag med samlad RL-rörelse på minst +4 är kandidater för nästa fas.
        </p>
        {redoForÖverlämning.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-6">
            Inga aktuella kandidater just nu.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {redoForÖverlämning.map((b) => (
              <ÖverlämningsKort key={b.id} b={b} />
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-card border border-border p-6">
        <h2 className="font-display text-lg mb-4">Alla i {fas}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {bolag.map((b) => (
            <ÖverlämningsKort key={b.id} b={b} />
          ))}
          {bolag.length === 0 && (
            <div className="text-sm text-muted-foreground col-span-full text-center py-6">
              Inga bolag i denna fas.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ÖverlämningsKort({ b }: { b: Bolag }) {
  const d = rlDelta(b);
  const [open, setOpen] = useState(false);
  return (
    <div className="p-3 rounded-xl bg-card border border-border hover:border-foreground/40 hover:shadow-md transition-all">
      <Link to="/bolag/$id" params={{ id: b.id }} className="flex items-center gap-2.5">
        <CompanyAvatar initialer={b.initialer} farg={b.logoFarg} size={36} />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate">{b.namn}</div>
          <div className="text-[11px] text-muted-foreground truncate">
            {b.kommun ?? b.ort} · {b.inflöde ?? "—"}
          </div>
        </div>
      </Link>
      <div className="mt-2.5 flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">{b.mognad}</span>
        {d && (
          <span className="flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
            <TrendingUp className="size-3" />
            RL +{d.total}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[11px] py-1.5 rounded-md bg-secondary hover:bg-secondary/70 text-foreground transition-colors"
      >
        <BarChart3 className="size-3" /> Visa RL-analys
      </button>
      <RLDetaljDialog b={b} open={open} onOpenChange={setOpen} />
    </div>
  );
}

function RLDetaljDialog({
  b,
  open,
  onOpenChange,
}: {
  b: Bolag;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const historik = b.rlHistorik ?? [];
  const d = rlDelta(b);
  const first = historik[0];
  const last = historik[historik.length - 1];
  const data = historik.map((p) => ({
    datum: new Date(p.datum).toLocaleDateString("sv-SE", { month: "short", year: "2-digit" }),
    BRL: p.brl,
    MRL: p.mrl,
    SRL: p.srl,
    TRL: p.trl,
  }));

  const axlar = [
    { key: "BRL" as const, label: "Business", color: "oklch(0.65 0.18 250)" },
    { key: "MRL" as const, label: "Market", color: "oklch(0.7 0.16 50)" },
    { key: "SRL" as const, label: "Sustainability", color: "oklch(0.7 0.16 140)" },
    { key: "TRL" as const, label: "Technology", color: "oklch(0.65 0.2 320)" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <CompanyAvatar initialer={b.initialer} farg={b.logoFarg} size={32} />
            <span>{b.namn} — RL-analys</span>
          </DialogTitle>
          <DialogDescription>
            Underlag för varför bolaget föreslås — readiness-level-utveckling över tid.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {d &&
            axlar.map((a) => {
              const v = d[a.key.toLowerCase() as "brl" | "mrl" | "srl" | "trl"];
              return (
                <div key={a.key} className="rounded-lg bg-secondary/50 p-2.5 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {a.key}
                  </div>
                  <div
                    className={`font-display text-lg ${
                      v >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"
                    }`}
                  >
                    {v >= 0 ? "+" : ""}
                    {v}
                  </div>
                  {first && last && (
                    <div className="text-[10px] text-muted-foreground">
                      {first[a.key.toLowerCase() as "brl" | "mrl" | "srl" | "trl"]} →{" "}
                      {last[a.key.toLowerCase() as "brl" | "mrl" | "srl" | "trl"]}
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        {data.length >= 2 ? (
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={data} margin={{ left: -10, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="datum"
                  stroke="var(--muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  domain={[0, 9]}
                  stroke="var(--muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {axlar.map((a) => (
                  <Line
                    key={a.key}
                    type="monotone"
                    dataKey={a.key}
                    stroke={a.color}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
            Ingen RL-historik registrerad än
          </div>
        )}

        {d && (
          <div className="rounded-lg border border-border bg-secondary/30 p-3 text-sm">
            <div className="font-medium mb-1">Varför föreslås {b.namn}?</div>
            <p className="text-muted-foreground text-xs">
              Samlad RL-rörelse på{" "}
              <span className="text-foreground font-medium">
                {d.total >= 0 ? "+" : ""}
                {d.total}
              </span>{" "}
              över {historik.length} avläsningar. Starkaste utveckling:{" "}
              <span className="text-foreground font-medium">
                {
                  axlar
                    .map((a) => ({
                      k: a.key,
                      v: d[a.key.toLowerCase() as "brl" | "mrl" | "srl" | "trl"],
                    }))
                    .sort((x, y) => y.v - x.v)[0]?.k
                }
              </span>
              .
            </p>
          </div>
        )}

        {historik.length >= 2 && (() => {
          const prev = historik[historik.length - 2]!;
          const diffs = axlar.map((a) => {
            const k = a.key.toLowerCase() as "brl" | "mrl" | "srl" | "trl";
            return { key: a.key, label: a.label, from: prev[k], to: last![k], diff: last![k] - prev[k] };
          });
          const störst = [...diffs].sort((x, y) => Math.abs(y.diff) - Math.abs(x.diff))[0]!;
          const period = `${new Date(prev.datum).toLocaleDateString("sv-SE", { month: "short", year: "2-digit" })} → ${new Date(last!.datum).toLocaleDateString("sv-SE", { month: "short", year: "2-digit" })}`;
          return (
            <div className="rounded-lg border border-border bg-secondary/30 p-3 text-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium">Största avvikelse senaste perioden</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{period}</div>
              </div>
              {störst.diff === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Inga förändringar mellan de två senaste avläsningarna.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  <span className="text-foreground font-medium">{störst.key}</span> ({störst.label}) rörde sig mest:{" "}
                  <span className={störst.diff > 0 ? "text-emerald-600 dark:text-emerald-400 font-medium" : "text-destructive font-medium"}>
                    {störst.from} → {störst.to} ({störst.diff > 0 ? "+" : ""}{störst.diff})
                  </span>
                  .
                </p>
              )}
              <div className="grid grid-cols-4 gap-2 mt-3">
                {diffs.map((dd) => (
                  <div key={dd.key} className="rounded-md bg-background/50 p-2 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{dd.key}</div>
                    <div className="text-[11px] text-muted-foreground">{dd.from} → {dd.to}</div>
                    <div className={`text-sm font-medium ${dd.diff > 0 ? "text-emerald-600 dark:text-emerald-400" : dd.diff < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                      {dd.diff > 0 ? "+" : ""}{dd.diff}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground space-y-2">
          <div className="font-medium text-foreground">Så tolkar du RL-rörelser</div>
          <p>
            Skalan 1–9 mäter mognad per dimension. <span className="text-emerald-600 dark:text-emerald-400 font-medium">Positiv rörelse</span> betyder att bolaget flyttat fram sin position — validerat antagande, säkrat kund, höjt teknikmognad eller stärkt hållbarhetsarbetet. <span className="text-destructive font-medium">Negativ rörelse</span> betyder omvärdering nedåt: tidigare bedömning visade sig optimistisk, en pivot har återställt mognaden, eller en pilot/kund föll bort.
          </p>
          <ul className="space-y-1 pl-4 list-disc">
            <li><span className="text-foreground font-medium">BRL</span> — affärsmodell &amp; ekonomi. Stigande BRL ⇒ närmare betalande kunder och hållbar enhets­ekonomi.</li>
            <li><span className="text-foreground font-medium">MRL</span> — marknad &amp; kundvalidering. Stigande MRL ⇒ tydligare ICP, fler verifierade behov.</li>
            <li><span className="text-foreground font-medium">SRL</span> — hållbarhet &amp; samhälls­nytta. Stigande SRL ⇒ stärkt impact-case, mätbara effekter.</li>
            <li><span className="text-foreground font-medium">TRL</span> — teknik &amp; produkt. Stigande TRL ⇒ från idé mot driftsatt lösning.</li>
          </ul>
          <p>
            <span className="text-foreground font-medium">Beslutsstöd:</span> bred positiv rörelse (≥3 dimensioner upp) talar för fasbyte eller utökat åtagande. Stor enskild rörelse pekar ut var coachning gett effekt — eller var ett akut gap kvarstår. Negativ rörelse ska alltid följas av samtal innan beslut: är det en sund justering eller en varningssignal?
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Stäng
          </Button>
          <Button asChild>
            <Link to="/bolag/$id" params={{ id: b.id }}>
              Öppna bolagskort
            </Link>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Nyckeltal({
  label,
  värde,
  ton,
}: {
  label: string;
  värde: number | string;
  ton?: "positive";
}) {
  return (
    <div className="rounded-2xl bg-card border border-border p-5">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={`mt-2 font-display text-3xl ${
          ton === "positive" ? "text-emerald-600 dark:text-emerald-400" : ""
        }`}
      >
        {värde}
      </div>
    </div>
  );
}
